import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { getSkillTitle } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Button } from '~/ui/core/button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconDelete, IconPlus } from '~/ui/core/icons';

import { useSkillsSearch } from '../../api/use-skills-search';
import { SkillSearchContentBase } from '../../job-profile-section/skills/SkillSearch';
import { useJobArchitectureUrlTileActions } from '../../url-tiles/use-job-architecture-url-tile-actions';
import type { ProfileTaskSkill } from '../model/profile-task';
import { profileTaskTranslationKeys } from '../profile-task-translations';
import { TaskSkillRow } from './TaskSkillRow';

export const TaskSkillSelector: FunctionComponent<{
    readonly activeLanguage: LanguageCodeForJSA;
    readonly knownSkills: readonly ProfileTaskSkill[];
    readonly onChange: (skillIds: readonly string[]) => void;
    readonly selectedSkillIds: readonly string[];
}> = ({ activeLanguage, knownSkills, onChange, selectedSkillIds }) => {
    const translate = useTranslate();
    const { openSkillTile } = useJobArchitectureUrlTileActions();
    const { applicationState } = useApplicationContext();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSkills, setSelectedSkills] = useState<ReadonlyMap<string, ProfileTaskSkill>>(
        () => new Map(knownSkills.map((skill) => [skill.skillId, skill])),
    );
    const search = useSkillsSearch({ acceptLanguage: applicationState.language.code, searchTerm });
    const results = search.data ?? [];

    return (
        <section className="flex flex-col gap-m">
            <div className="flex items-center justify-between gap-m">
                <h3 className="m-0 text-body-xs font-medium text-text-primary">
                    {translate(profileTaskTranslationKeys.skills)}
                </h3>
                <DropdownMenu onOpenChange={(open) => !open && setSearchTerm('')}>
                    <DropdownMenuTrigger>
                        <Button iconPosition="leading" size="sm" variant="secondary">
                            <IconPlus />
                            {translate('common.edit')}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="p-0" sideOffset={5}>
                        <SkillSearchContentBase
                            displayedSkills={results}
                            inputId={`profile-task-skill-search-${selectedSkillIds.length}`}
                            isLoading={search.isLoading}
                            isSkillSelected={(skillId) => selectedSkillIds.includes(skillId)}
                            languageCode={activeLanguage}
                            placeholder={translate(profileTaskTranslationKeys.searchSkills)}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            onSkillSelect={(skillId) => {
                                const skill = results.find((candidate) => candidate.id === skillId);
                                if (skill === undefined || selectedSkillIds.includes(skillId)) {
                                    return;
                                }
                                setSelectedSkills((previous) =>
                                    new Map(previous).set(skillId, {
                                        skillId,
                                        title: getSkillTitle(skill, activeLanguage),
                                        type: skill.skillType,
                                    }),
                                );
                                onChange([...selectedSkillIds, skillId]);
                                setSearchTerm('');
                            }}
                        />
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            {selectedSkillIds.length === 0 ? null : (
                <ul className="m-0 flex list-none flex-col p-0" data-testid="profile-task-selected-skills">
                    {selectedSkillIds.map((skillId) => {
                        const skill = selectedSkills.get(skillId);
                        const removeButton = (
                            <Button
                                aria-label={translate(profileTaskTranslationKeys.removeSkill)}
                                iconOnly={true}
                                size="sm"
                                variant="tertiary"
                                onClick={() => onChange(selectedSkillIds.filter((candidate) => candidate !== skillId))}
                            >
                                <IconDelete />
                            </Button>
                        );

                        return (
                            <li key={skillId} className="list-none">
                                {skill === undefined ? (
                                    <div className="flex w-full items-center justify-between gap-l py-s">
                                        <span className="text-sm">{skillId}</span>
                                        {removeButton}
                                    </div>
                                ) : (
                                    <TaskSkillRow
                                        skill={skill}
                                        trailing={removeButton}
                                        onClick={() => openSkillTile(skill.skillId, activeLanguage, skill.level)}
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};
